import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/lib/api'

const POLL_INTERVAL_MS = 60_000

interface NotificationBadgesContextValue {
  unreadMessageCount: number
  pendingSignatureCount: number
}

const NotificationBadgesContext = createContext<NotificationBadgesContextValue | null>(null)

/**
 * Lives above the router (see main.tsx) so it mounts once per session
 * instead of once per page -- every page used to render its own <AppShell>,
 * which re-fetched these two counts (including the conversations list, one
 * of the heavier endpoints in the app) on every single navigation. Polling
 * now happens here, once, and every page just reads the current counts.
 */
export function NotificationBadgesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [pendingSignatureCount, setPendingSignatureCount] = useState(0)

  useEffect(() => {
    if (!user) {
      setUnreadMessageCount(0)
      setPendingSignatureCount(0)
      return
    }

    function refresh() {
      api
        .get<{ data: { unread_count: number }[] }>('/conversations')
        .then((res) => setUnreadMessageCount(res.data.data.reduce((sum, c) => sum + c.unread_count, 0)))
        .catch(() => {
          // Le badge reste inchangé en cas d'échec.
        })

      api
        .get<{ count: number }>('/signature-requests/pending-for-me-count')
        .then((res) => setPendingSignatureCount(res.data.count))
        .catch(() => {
          // Le badge reste inchangé en cas d'échec.
        })
    }

    refresh()
    const interval = window.setInterval(refresh, POLL_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [user])

  return (
    <NotificationBadgesContext.Provider value={{ unreadMessageCount, pendingSignatureCount }}>
      {children}
    </NotificationBadgesContext.Provider>
  )
}

export function useNotificationBadges(): NotificationBadgesContextValue {
  const context = useContext(NotificationBadgesContext)
  if (!context) {
    throw new Error('useNotificationBadges must be used within a NotificationBadgesProvider')
  }
  return context
}
